/**
 * <cos-lazy-css> — wraps lazy-loaded <link rel="preload"> tags emitted by
 * snippets/css-preload.liquid (lazy_on_scroll branch) and swaps them to
 * rel="stylesheet" when the host shopify-section nears the viewport
 * (IntersectionObserver, default rootMargin 800px).
 *
 * Attributes:
 *   token         — value that matches data-css-lazy on the contained links
 *   root-margin   — IO rootMargin (default "800px 0px")
 *   reveal-section — when present, hide the [data-css-pending] descendant
 *                    inside the host section until the stylesheet load fires.
 *                    Pairs with an eager rule in main.css that sets
 *                    visibility:hidden + opacity:0 on [data-css-pending].
 *
 * Usage (emitted by css-preload.liquid):
 *   <cos-lazy-css token="pl-{{ section.id }}" reveal-section>
 *     <link rel="preload" href="..." as="style" data-css-lazy="pl-{{ section.id }}">
 *   </cos-lazy-css>
 */
class CosLazyCss extends HTMLElement {
  connectedCallback() {
    const token = this.getAttribute('token');
    if (!token) return;

    const links = this.querySelectorAll(`link[data-css-lazy="${token}"]`);
    if (!links.length) return;

    const section = this.closest('[id^="shopify-section-"]');
    const reveal = this.hasAttribute('reveal-section');
    const target = reveal && section ? section.querySelector('[data-css-pending]') : null;

    let loaded = 0;
    let revealed = false;
    const doReveal = () => {
      if (revealed || !target) return;
      revealed = true;
      target.removeAttribute('data-css-pending');
    };

    const swap = () => {
      links.forEach((link) => {
        link.addEventListener('load', () => {
          if (++loaded >= links.length) doReveal();
        }, { once: true });
        link.addEventListener('error', doReveal, { once: true });
        link.rel = 'stylesheet';
      });
      // Safety net: if load event never fires (e.g. cached preload), reveal anyway.
      if (target) setTimeout(doReveal, 3000);
    };

    if (!('IntersectionObserver' in window) || !section) {
      swap();
      return;
    }

    const margin = this.getAttribute('root-margin') || '800px 0px';
    this._io = new IntersectionObserver((entries) => {
      for (let i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          swap();
          this._io.disconnect();
          return;
        }
      }
    }, { rootMargin: margin });
    this._io.observe(section);
  }

  disconnectedCallback() {
    if (this._io) {
      this._io.disconnect();
      this._io = null;
    }
  }
}

if (!customElements.get('cos-lazy-css')) {
  customElements.define('cos-lazy-css', CosLazyCss);
}
